import { Commodity, LoadStopTypes, NormalizedLoad, Unit } from 'types/Load';
import { MaybeUndef, Undef } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';

import { globalTranzEquipmentList } from './constants';

const KG_TO_LBS = 2.20462;

// Time range configuration for different stop types
interface TimeRange {
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
}

/** Full day 00:00 - 23:30 (30-min steps); used in main load form so open/close can be same. */
const FULL_DAY: TimeRange = {
  startHour: 0,
  startMinute: 0,
  endHour: 23,
  endMinute: 30,
};

/** Main form: full day for both open and close. */
const TIME_RANGES_MAIN_FORM: Record<
  LoadStopTypes,
  { open: TimeRange; close: TimeRange }
> = {
  [LoadStopTypes.Pickup]: { open: FULL_DAY, close: FULL_DAY },
  [LoadStopTypes.Consignee]: { open: FULL_DAY, close: FULL_DAY },
};

/** Create Location dialog: restricted ranges (e.g. pickup open 06:00-19:00, close 08:00-21:00). */
const TIME_RANGES_CREATE_LOCATION: Record<
  LoadStopTypes,
  { open: TimeRange; close: TimeRange }
> = {
  [LoadStopTypes.Pickup]: {
    open: { startHour: 6, startMinute: 0, endHour: 19, endMinute: 0 },
    close: { startHour: 8, startMinute: 0, endHour: 21, endMinute: 0 },
  },
  [LoadStopTypes.Consignee]: {
    open: { startHour: 6, startMinute: 0, endHour: 18, endMinute: 30 },
    close: { startHour: 6, startMinute: 30, endHour: 19, endMinute: 0 },
  },
};

/**
 * Generate time options in HH:MM format based on stop type and time type
 * @param stopType - The stop type (Pickup or Consignee)
 * @param isCloseTime - Whether to generate close time options (default: false for open time)
 * @param options - useFullDay: true = main form (00:00-23:30); false = Create Location dialog (restricted)
 * @returns Array of time strings in HH:MM format
 */
export function generateTimeOptions(
  stopType?: LoadStopTypes,
  isCloseTime?: boolean,
  options?: { useFullDay?: boolean }
): string[] {
  const type = stopType || LoadStopTypes.Pickup;
  const timeType = isCloseTime ? 'close' : 'open';
  const ranges =
    options?.useFullDay === false
      ? TIME_RANGES_CREATE_LOCATION
      : TIME_RANGES_MAIN_FORM;
  const range = ranges[type]?.[timeType];

  if (!range) return [];

  const times: string[] = [];
  const { startHour, startMinute, endHour, endMinute } = range;

  for (let hour = startHour; hour <= endHour; hour++) {
    const isFirstHour = hour === startHour;
    const isLastHour = hour === endHour;
    const startMin = isFirstHour ? startMinute : 0;
    const maxMinute = isLastHour ? endMinute : 30; // 30 is max for non-last hours (00 or 30)

    for (let minute = startMin; minute <= maxMinute; minute += 30) {
      times.push(
        `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      );
    }
  }

  return times;
}

/**
 * Get default open and close times based on stop type
 * @param stopType - The stop type (Pickup or Consignee)
 * @returns Object with openTime and closeTime strings
 */
export function getDefaultTimes(stopType?: LoadStopTypes): {
  openTime: string;
  closeTime: string;
} {
  if (stopType === LoadStopTypes.Consignee) {
    return { openTime: '06:00', closeTime: '19:00' };
  } else {
    return { openTime: '06:00', closeTime: '21:00' };
  }
}

/**
 * Convert openTime/closeTime (Maybe<Date> | string) to HH:MM for BE payload.
 * Handles HH:MM strings, Date objects, and ISO/date strings (e.g. "2024-01-15T06:00:00Z").
 * For Date/ISO we use UTC hours/minutes so the same time the user selected (or that was stored)
 * is sent to BE/GTZ regardless of browser timezone.
 */
export function toHHMMString(v: MaybeUndef<Date | string>): string {
  if (v == null) return '';
  if (typeof v === 'string' && /^\d{1,2}:\d{2}$/.test(v)) {
    const [h, m] = v.split(':');
    return `${h.padStart(2, '0')}:${(m ?? '').padStart(2, '0')}`;
  }
  if (v instanceof Date) {
    return `${v.getUTCHours().toString().padStart(2, '0')}:${v.getUTCMinutes().toString().padStart(2, '0')}`;
  }
  if (typeof v === 'string') {
    const date = new Date(v);
    if (!isNaN(date.getTime())) {
      return `${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')}`;
    }
  }
  return '';
}

/** Backend returns 404 with these messages when customer has no commodities/locations yet (expected case). */
export function isExpectedEmptyResult(error: ApiError): boolean {
  const msg = (error?.message ?? '').toLowerCase();
  return (
    error?.status === 404 ||
    msg.includes('no new or updated commodities found') ||
    msg.includes('no new or updated locations found')
  );
}

export function handleCommodityOrLocationFetchError(
  error: ApiError,
  context: 'commodities' | 'locations',
  toast: (opts: { description: string; variant: 'destructive' }) => void,
  options?: { isManualRefresh?: boolean }
): void {
  const isManualRefresh = options?.isManualRefresh ?? false;

  // Manual refresh: always surface errors (user explicitly requested data)
  if (isManualRefresh) {
    toast({
      description: error.message || `Error while refreshing ${context} list.`,
      variant: 'destructive',
    });
    return;
  }

  // Auto-fetch (initial load, customer change): expected 404 = no toast; real errors = destructive
  if (isExpectedEmptyResult(error)) {
    return; // No toast - avoids noise when switching to customers without commodities/locations
  }
  toast({
    description: error.message || `Error while fetching ${context} list.`,
    variant: 'destructive',
  });
}

/**
 * Title-case a string (e.g. "VAN" -> "Van", "FLAT BED" -> "Flat Bed").
 * Used when normalizing equipment/transport type from AI suggestions.
 */
function titleCase(s: string): string {
  if (!s?.trim()) return s ?? '';
  return s
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Normalize equipment/transport type from AI suggestions so it matches GlobalTranz dropdown options (e.g. "VAN" -> "Van").
 * When equipmentOptions is provided (e.g. from API), matches case-insensitively against those; otherwise uses globalTranzEquipmentList.
 *
 * @param value - Raw value from suggestion (e.g. "VAN", "van")
 * @param equipmentOptions - Optional list of options with displayText (e.g. from getBootstrapGlobalTranz); when provided, returned value will match one of these exactly
 * @returns Canonical display string matching an option, or title-cased value if no match
 */
export function normalizeGlobalTranzTransportType(
  value: Undef<string>,
  equipmentOptions?: { displayText: string }[]
): string {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return '';

  const lower = trimmed.toLowerCase();

  if (equipmentOptions?.length) {
    const match = equipmentOptions.find(
      (o) => o.displayText.toLowerCase() === lower
    );
    if (match) return match.displayText;
    return titleCase(trimmed);
  }

  const listMatch = globalTranzEquipmentList.find(
    (item) => item.toLowerCase() === lower
  );
  if (listMatch) return listMatch;
  return titleCase(trimmed);
}

export function isPalletCommodity(
  packagingType: Undef<string>,
  pieceUnit: unknown
): boolean {
  const palletTypes = ['pallet', 'pallets', 'plt', 'plts'];
  const pkgType = (packagingType ?? '').trim().toLowerCase();
  const pctUnit = String(pieceUnit ?? '')
    .trim()
    .toLowerCase();

  return palletTypes.includes(pkgType) || palletTypes.includes(pctUnit);
}

function getCommodityWeightUnit(commodity: Commodity): Unit {
  const unit = String(
    commodity.weightUnit ??
      commodity.grossWeight?.unit ??
      commodity.netWeight?.unit ??
      ''
  ).toLowerCase();

  return unit === Unit.Kg ? Unit.Kg : Unit.Pounds;
}

function convertWeightToPounds(weight: number, unit: Unit): number {
  return unit === Unit.Kg ? weight * KG_TO_LBS : weight;
}

export function getTotalWeightFromCommodities(
  commodities: Undef<Commodity[]>
): Undef<{ val: number; unit: Unit }> {
  const weightedCommodities = (commodities ?? [])
    .map((commodity) => ({
      weight: Number(commodity.weightTotal),
      unit: getCommodityWeightUnit(commodity),
    }))
    .filter(({ weight }) => Number.isFinite(weight) && weight > 0);

  if (!weightedCommodities.length) {
    return undefined;
  }

  const hasKg = weightedCommodities.some(({ unit }) => unit === Unit.Kg);
  const hasPounds = weightedCommodities.some(
    ({ unit }) => unit === Unit.Pounds
  );
  const hasMixedUnits = hasKg && hasPounds;
  const totalWeight = weightedCommodities.reduce(
    (sum, { weight, unit }) =>
      sum + (hasMixedUnits ? convertWeightToPounds(weight, unit) : weight),
    0
  );

  return {
    val: totalWeight,
    unit: hasKg && !hasPounds ? Unit.Kg : Unit.Pounds,
  };
}

export function withCommodityTotalWeight(data: NormalizedLoad): NormalizedLoad {
  const existingTotalWeight = Number(data.specifications?.totalWeight?.val);
  if (Number.isFinite(existingTotalWeight) && existingTotalWeight > 0) {
    return data;
  }

  const totalWeight = getTotalWeightFromCommodities(data.commodities);
  if (!totalWeight) {
    return data;
  }

  return {
    ...data,
    specifications: {
      ...data.specifications,
      totalWeight,
    },
  };
}

/**
 * When a commodity is palletized and `handlingQuantity` is unset:
 * - Prefer `totalPieces.val` when present (suggestions often put pallet count there when `totalPieces.unit` is pallets)
 * - Else if `quantity` is set, treat it as mis-filed pallet count: move into `handlingQuantity` and clear `quantity`
 */
export const mapCommodityDefaults = (commodities: Commodity[]): Commodity[] =>
  commodities.map((c) => {
    const pieceUnit =
      c.totalPieces?.unit != null ? String(c.totalPieces.unit) : undefined;

    if (!isPalletCommodity(c.packagingType, pieceUnit)) {
      return c;
    }

    const handlingQty = Number(c.handlingQuantity);
    if (Number.isFinite(handlingQty) && handlingQty > 0) {
      return c;
    }

    const quantity = Number(c.quantity);
    const totalPiecesVal = Number(c.totalPieces?.val);
    if (Number.isFinite(totalPiecesVal) && totalPiecesVal > 0) {
      return {
        ...c,
        handlingQuantity: totalPiecesVal,
        quantity: undefined as unknown as number,
      };
    }

    if (Number.isFinite(quantity) && quantity > 0) {
      return {
        ...c,
        handlingQuantity: quantity,
        quantity: undefined as unknown as number,
      };
    }

    return c;
  });
